import * as React from 'react';

/**
 * Lifecycle of a presence-transitioned element.
 *
 * The two `pre*` stages exist so the element can commit its transition-start
 * styles (and measure the DOM) one frame before the target styles land —
 * without them the browser coalesces both style writes and no transition runs.
 */
export type PresenceStage = 'preEnter' | 'entering' | 'open' | 'preExit' | 'exiting';

/** {@link useTransitionedPresence}'s internal state, exported for the pure reducers below. */
export interface IPresenceState<T> {
  /** What to render — retains the last non-null value through the exit stages. */
  renderValue: T | null;
  /** Meaningful only while `renderValue` is non-null. */
  stage: PresenceStage;
}

/** How long past `durationMs` the fallback timer waits before force-settling. */
const SETTLE_TIMEOUT_MARGIN_MS = 150;

/** A value appeared (or changed). An interrupted exit re-enters from wherever the transition left it. */
export const presenceOnShow = <T>(state: IPresenceState<T>, value: T): IPresenceState<T> => {
  if (state.renderValue === null) {
    return { renderValue: value, stage: 'preEnter' };
  }

  if (state.stage === 'preExit' || state.stage === 'exiting') {
    return { renderValue: value, stage: 'entering' };
  }

  return { renderValue: value, stage: state.stage };
};

/** The value went null. An interrupted enter exits from wherever the transition left it. */
export const presenceOnHide = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.renderValue === null || state.stage === 'preExit' || state.stage === 'exiting') {
    return state;
  }

  if (state.stage === 'open') {
    return { ...state, stage: 'preExit' };
  }

  return { ...state, stage: 'exiting' };
};

/**
 * A running transition finished (or the fallback timer fired): an enter lands
 * on `open`, an exit unmounts. Also settles a `pre*` stage whose rAF never ran
 * (throttled background tab) so the element cannot get stuck invisible.
 */
export const presenceOnSettle = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.renderValue === null || state.stage === 'open') {
    return state;
  }

  if (state.stage === 'preExit' || state.stage === 'exiting') {
    return { renderValue: null, stage: 'open' };
  }

  return { ...state, stage: 'open' };
};

/** The pre → active flips the double-rAF drives. Anything else is left for {@link presenceOnSettle}. */
export const presenceOnFrame = <T>(state: IPresenceState<T>): IPresenceState<T> => {
  if (state.stage === 'preEnter') {
    return { ...state, stage: 'entering' };
  }

  if (state.stage === 'preExit') {
    return { ...state, stage: 'exiting' };
  }

  return state;
};

interface ITransitionedPresenceOptions {
  /**
   * The CSS property whose `transitionend` settles a stage (e.g. `'width'`,
   * `'translate'`). Other properties finishing on the same element — and any
   * event bubbling up from a child — are ignored, so a shorter child fade
   * cannot truncate the element's own transition.
   */
  transitionProperty: string;
  /** The CSS transition's duration. The fallback timer fires this plus a margin after each stage starts. */
  durationMs: number;
}

export interface ITransitionedPresence<T> {
  /** The content to render — holds the last non-null value through the exit. Null once fully closed. */
  renderValue: T | null;
  /** Which styles the element should wear this frame. */
  stage: PresenceStage;
  /** Attach to the transitioning element itself; settles the enter and unmounts after the exit. */
  onTransitionEnd: React.TransitionEventHandler;
}

const prefersReducedMotion = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Keeps an element mounted through a CSS enter/exit transition and tells it
 * which stage of that transition it is in.
 *
 * The caller maps stages to styles: transition-start styles on `preEnter` /
 * `exiting`, target styles on `entering` / `open` / `preExit`. A first render
 * with a non-null value starts at `open` with no animation (nothing should
 * slide on page load), reversals mid-transition animate back from wherever the
 * transition currently is without unmounting, and a `prefers-reduced-motion`
 * viewer gets instant mounts and unmounts with no staging at all.
 */
export const useTransitionedPresence = <T>(
  value: T | null,
  { transitionProperty, durationMs }: ITransitionedPresenceOptions
): ITransitionedPresence<T> => {
  const [state, setState] = React.useState<IPresenceState<T>>({ renderValue: value, stage: 'open' });

  const prevValueRef = React.useRef(value);
  React.useEffect(() => {
    if (value === prevValueRef.current) {
      return;
    }
    prevValueRef.current = value;

    if (prefersReducedMotion()) {
      setState({ renderValue: value, stage: 'open' });
      return;
    }

    setState((s) => (value === null ? presenceOnHide(s) : presenceOnShow(s, value)));
  }, [value]);

  const { renderValue, stage } = state;
  const isTransitioning = renderValue !== null && stage !== 'open';

  React.useEffect(() => {
    if (!isTransitioning) {
      return;
    }

    // Double rAF: the first fires just before the paint that commits the
    // pre-stage styles, so the flip lands on the following frame and the
    // browser sees two distinct style states to transition between.
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => setState(presenceOnFrame));
    });

    const settleTimer = setTimeout(() => setState(presenceOnSettle), durationMs + SETTLE_TIMEOUT_MARGIN_MS);

    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) {
        cancelAnimationFrame(secondFrame);
      }
      clearTimeout(settleTimer);
    };
    // `stage` re-arms the timer per stage; the pre-stage's timer would
    // otherwise fire mid-transition after the rAF flip consumed no time.
  }, [isTransitioning, stage, durationMs]);

  const onTransitionEnd = React.useCallback<React.TransitionEventHandler>(
    (e) => {
      if (e.target !== e.currentTarget || e.propertyName !== transitionProperty) {
        return;
      }
      setState(presenceOnSettle);
    },
    [transitionProperty]
  );

  return { renderValue, stage, onTransitionEnd };
};
